import { Autocomplete, TextField, InputAdornment } from '@mui/material';
import React from 'react';

const AutocompleteComponent = ({
  // Core props
  options = [],
  value = null,
  onChange = () => { },
  loading = false,
  placeholder = "Select an option",

  // Label props
  label = "",
  labelClassName = "font-inter font-medium not-italic text-[14px] leading-[1.2] tracking-[0] text-[#1F2937] mb-2",
  showLabel = false,

  // Styling props
  width = 565,
  height = 46,
  containerStyle = {},

  // Icon props
  startIcon = null,
  startIconAlt = "icon",
  startIconSize = { width: 20, height: 20 },
  dropdownIcon = "/chevronDown.svg",
  dropdownIconAlt = "dropdown",
  dropdownIconSize = { width: 20, height: 20 },

  // Option rendering props
  getOptionLabel = (option) => option.type === "separator" ? option.label : (option.name || ""),
  isOptionEqualToValue = (option, value) => option?.adsetId === value?.adsetId,

  // Custom option rendering
  renderOptionContent = null, // Custom function to render option content

  // Option styling props
  separatorStyle = {
    padding: '8px 0 8px 12px',
    textAlign: 'left',
    fontWeight: 600,
    color: '#6B7280',
    background: '#F3F4F6',
    borderRadius: '6px',
    fontSize: '13px',
    margin: '4px 0',
  },
  optionStyle = {
    minHeight: 50,
    height: 'auto',
    whiteSpace: 'normal',
    alignItems: 'flex-start',
    marginBottom: '4px'
  },
  optionClassName = "w-full rounded-lg p-3 gap-1 opacity-100 flex items-center box-border",

  // Option text styling
  primaryTextStyle = {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontStyle: 'normal',
    fontSize: 16,
    lineHeight: '140%',
    textAlign: 'left',
    width: '100%',
    display: 'block',
  },
  secondaryTextStyle = {
    marginTop: 4,
    marginBottom: 4,
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: 16,
    lineHeight: '100%',
    color: '#6B7280',
  },

  // Data field mappings
  primaryField = "name",
  secondaryField = "campaignName", // Can be null to hide secondary text
  separatorField = "label",

  // Validation
  required = false,
  error = false,
  helperText = "",

  // Input styling
  borderColor = '#D7D7D7',
  errorBorderColor = '#EF4444',
  backgroundColor = '#fff',
  disabledBackgroundColor = '#F9FAFB',
  borderRadius = '8px',
  boxShadow = '0px 1px 2px 0px #0000000A',

  // Dropdown styling
  dropdownWidth = null, // Uses width if null
  dropdownBorderColor = '#D7D7D7',
  dropdownPadding = '8px',
  dropdownBorderRadius = '8px',
  dropdownMarginTop = '8px',

  // Additional props
  disabled = false,
  size = "small",
  variant = "outlined",
  ...otherProps
}) => {

  const defaultRenderOption = (props, option, { selected }) => {
    if (option.type === "separator") {
      return (
        <li key={option[separatorField] || option.label} style={separatorStyle}>
          {option[separatorField] || option.label}
        </li>
      );
    }

    // Use a unique key for each option
    const { key, ...rest } = props;
    const uniqueKey = option.id || option.adsetId || key;

    // Custom render function provided by parent
    if (renderOptionContent) {
      return (
        <li
          key={uniqueKey}
          {...rest}
          className={`${optionClassName} ${selected ? 'bg-gray-100' : ''}`}
          style={optionStyle}
          onMouseEnter={(e) => {
            e.currentTarget.classList.add('bg-gray-100');
          }}
          onMouseLeave={(e) => {
            if (!selected) e.currentTarget.classList.remove('bg-gray-100');
          }}
        >
          {renderOptionContent(option, selected)}
        </li>
      );
    }

    return (
      <li
        key={uniqueKey}
        {...rest}
        className={`${optionClassName} ${selected ? 'bg-gray-100' : ''}`}
        style={optionStyle}
        onMouseEnter={(e) => {
          e.currentTarget.classList.add('bg-gray-100');
        }}
        onMouseLeave={(e) => {
          if (!selected) e.currentTarget.classList.remove('bg-gray-100');
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={primaryTextStyle}>
            {option[primaryField] || ''}
          </span>
          {secondaryField && option[secondaryField] && (
            <span style={secondaryTextStyle}>
              {option[secondaryField]}
            </span>
          )}
        </div>
      </li>
    );
  };

  return (
    <div style={{ width, ...containerStyle }}>
      {showLabel && label && (
        <div className={labelClassName}>
          {label}
        </div>
      )}

      <Autocomplete
        options={options}
        loading={loading}
        value={value}
        onChange={(event, newValue) => {
          // Prevent selecting the separator
          if (newValue && newValue.type === "separator") return;
          onChange(event, newValue);
        }}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        disabled={disabled}
        size={size}
        popupIcon={
          <img
            src={dropdownIcon}
            alt={dropdownIconAlt}
            style={dropdownIconSize}
          />
        }
        sx={{
          width,
          height,
          borderRadius,
          borderColor: error ? errorBorderColor : borderColor,
          justifyContent: 'space-between',
          boxShadow,
          opacity: 1,
          '& .MuiOutlinedInput-root': {
            borderRadius,
            borderColor: error ? errorBorderColor : borderColor,
            minHeight: height,
            paddingTop: '12px',
            paddingBottom: '12px',
            boxShadow,
            backgroundColor: disabled ? disabledBackgroundColor : backgroundColor,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: error ? errorBorderColor : borderColor,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: error ? errorBorderColor : borderColor,
              borderWidth: '1px'
            },
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: error ? errorBorderColor : borderColor,
            },
            '&.Mui-focused': {
              outline: 'none',
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: error ? errorBorderColor : borderColor,
          },
        }}
        slotProps={{
          paper: {
            sx: {
              mt: dropdownMarginTop,
              border: `1px solid ${dropdownBorderColor}`,
              boxSizing: 'border-box',
              padding: dropdownPadding,
              borderRadius: dropdownBorderRadius,
              width: dropdownWidth || width,
              overflowX: 'hidden',
            },
          },
        }}
        ListboxProps={{
          style: {
            padding: 0,
            overflowX: 'hidden',
          },
        }}
        renderOption={defaultRenderOption}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            variant={variant}
            required={required}
            size={size}
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              ...(startIcon && {
                startAdornment: (
                  <InputAdornment position="start" sx={{ marginRight: 0 }}>
                    <span className="flex items-center gap-2 pl-3 pr-2">
                      <img
                        src={startIcon}
                        alt={startIconAlt}
                        style={startIconSize}
                      />
                    </span>
                  </InputAdornment>
                ),
              }),
              style: {
                paddingLeft: startIcon ? 0 : 12,
                textAlign: 'left',
                outline: 'none'
              },
            }}
            inputProps={{
              ...params.inputProps,
              style: {
                ...params.inputProps?.style,
                paddingLeft: startIcon ? 0 : 12,
                textAlign: 'left',
                outline: 'none'
              },
            }}
          />
        )}
        {...otherProps}
      />
    </div>
  );
};

export default AutocompleteComponent;